import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { autoRedirectToLogin, clearLoginAttempt, type SessionKind } from '@/shared/api'
import { runtimeConfig } from '@/shared/config'
import { useDeviceTimezoneSync } from './use-device-timezone-sync'
import { useSession } from './use-session'

// Гейт страницы по контуру сессии. Без сессии уводим на единый вход хаба
// (`plan-customer/22`) сразу — своей формы логина у telemost нет, промежуточный
// экран с кнопкой лишний шаг. Вернулись со входа всё ещё без сессии
// (autoRedirectToLogin рвёт цикл) → брендированный /login с кнопкой и возвратом
// (?redirect=). Гостевые маршруты этот гейт не зовут: гость не hub-принципал и
// живёт без сессии.
// Чужой контур не выбрасывает, а уводит на его собственную главную: сотрудник и
// заказчик видят разные приложения, а не отказ.
// В mock-режиме сессия всегда есть; guard оставлен, чтобы dev-сценарии с упавшим
// /me не выкидывали из приложения.
function useRequireContour(kind: SessionKind, otherContourHome: '/meetings' | '/my/meetings') {
  const { isLoading, isAuthenticated, session } = useSession()
  const navigate = useNavigate()
  useEffect(() => {
    if (isLoading) return
    if (session && session.kind !== kind) {
      void navigate({ to: otherContourHome, replace: true })
      return
    }
    if (runtimeConfig.API_MODE === 'mock') return
    // Сессия жива — следующий её обрыв снова имеет право на автопереход.
    if (isAuthenticated) {
      clearLoginAttempt()
      return
    }
    if (autoRedirectToLogin()) return
    void navigate({
      to: '/login',
      search: { redirect: window.location.pathname + window.location.search },
      replace: true,
    })
  }, [isLoading, isAuthenticated, session, kind, otherContourHome, navigate])
}

// Страница сотрудника. Здесь же — единственная точка, где телемост сообщает
// хабу зону устройства: гейт и есть признак «за экраном аутентифицированный
// сотрудник». Гостевые маршруты (/guest, /join) этот гейт не зовут и зону не
// шлют — гость не hub-принципал.
export function useRequireSession(): void {
  useRequireContour('employee', '/my/meetings')
  useDeviceTimezoneSync()
}

// Страница заказчика: read-only витрина своих созвонов.
export function useRequireCustomer(): void {
  useRequireContour('customer', '/meetings')
}
